/**
 * QR Code Display Modal
 * Shows QR code for authentication (e.g., WhatsApp)
 * Polls for connection status while user scans
 */
import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Alert, AlertDescription } from '../ui/alert';
import { QrCode, Smartphone, CheckCircle, Loader2, RefreshCw } from 'lucide-react';

interface QRCodeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  serverName: string;
  qrData: string; // Base64 encoded QR image or QR text data
  sessionId?: string;
  pollStatusFunc: () => Promise<boolean>; // Function to check if auth completed
  description?: string;
}

const QRCodeModal: React.FC<QRCodeModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  serverName,
  qrData,
  sessionId,
  pollStatusFunc,
  description
}) => {
  const [status, setStatus] = useState<'waiting' | 'scanning' | 'connected' | 'error'>('waiting');
  const [error, setError] = useState<string | null>(null);
  const [timeLeft, setTimeLeft] = useState(180); // 3 minutes timeout
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (isOpen) {
      startPolling();
      startTimeout();
    }

    return () => {
      stopPolling();
      stopTimeout();
    };
  }, [isOpen]);

  const startPolling = () => {
    // Poll every 2 seconds for connection status
    pollIntervalRef.current = setInterval(async () => {
      try {
        const isConnected = await pollStatusFunc();
        if (isConnected) {
          setStatus('connected');
          stopPolling();
          stopTimeout();
          setTimeout(() => {
            onSuccess();
            onClose();
          }, 1500);
        }
      } catch (err) {
        console.error('Error polling status:', err);
        // Continue polling even if there's an error
      }
    }, 2000);
  };

  const stopPolling = () => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
  };

  const startTimeout = () => {
    // Countdown timer
    const countdown = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          clearInterval(countdown);
          setStatus('error');
          setError('QR code expired. Please try again.');
          stopPolling();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    // Overall timeout
    timeoutRef.current = setTimeout(() => {
      clearInterval(countdown);
      setStatus('error');
      setError('Authentication timeout. Please try again.');
      stopPolling();
    }, 180000); // 3 minutes
  };

  const stopTimeout = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  const handleRefresh = () => {
    // Refresh QR code (would need to call parent to get new QR)
    setStatus('waiting');
    setError(null);
    setTimeLeft(180);
    startPolling();
    startTimeout();
  };

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const renderQRCode = () => {
    // Check if qrData is base64 image
    if (qrData.startsWith('data:image') || qrData.includes('base64')) {
      return <img src={qrData} alt="QR Code" className="w-full h-full" />;
    }
    
    // Check if qrData is a URL (like http://localhost:8080/qr/...)
    if (qrData.startsWith('http://') || qrData.startsWith('https://')) {
      return <img src={qrData} alt="QR Code" className="w-full h-full" />;
    }
    
    // If it's text data, you'd need a QR code library to generate the image
    // For now, we'll show a placeholder
    return (
      <div className="flex items-center justify-center h-full bg-muted rounded-lg">
        <QrCode className="h-32 w-32 text-muted-foreground" />
      </div>
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Smartphone className="h-5 w-5" />
            QR Code Authentication
          </DialogTitle>
          <DialogDescription>
            {description || `Scan this QR code with ${serverName} to authenticate`}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {status === 'connected' ? (
            <div className="flex flex-col items-center justify-center py-8">
              <CheckCircle className="h-16 w-16 text-green-600 mb-4" />
              <p className="text-lg font-medium">Connected Successfully!</p>
              <p className="text-sm text-muted-foreground mt-2">
                Redirecting...
              </p>
            </div>
          ) : (
            <>
              <div className="relative">
                <div className="w-64 h-64 mx-auto bg-white p-4 rounded-lg border-2 border-border">
                  {renderQRCode()}
                </div>
                
                {status === 'waiting' && !qrData && (
                  <div className="absolute inset-0 flex items-center justify-center bg-background/80 rounded-lg">
                    <div className="text-center">
                      <Loader2 className="h-8 w-8 animate-spin mx-auto mb-2" />
                      <p className="text-sm">Waiting for scan...</p>
                    </div>
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">
                    Time remaining: {formatTime(timeLeft)}
                  </span>
                  {sessionId && (
                    <span className="text-xs text-muted-foreground">
                      Session: {sessionId.slice(0, 8)}...
                    </span>
                  )}
                </div>

                <Alert>
                  <AlertDescription>
                    <ol className="list-decimal list-inside space-y-1 text-sm">
                      <li>Open {serverName} on your phone</li>
                      <li>Navigate to Settings or Linked Devices</li>
                      <li>Scan this QR code</li>
                      <li>Confirm the connection</li>
                    </ol>
                  </AlertDescription>
                </Alert>
              </div>
            </>
          )}

          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
        </div>

        <DialogFooter>
          {status === 'error' ? (
            <>
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
              >
                Cancel
              </Button>
              <Button onClick={handleRefresh}>
                <RefreshCw className="h-4 w-4 mr-2" />
                Try Again
              </Button>
            </>
          ) : status !== 'connected' ? (
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
            >
              Cancel
            </Button>
          ) : null}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default QRCodeModal;